import { useEffect, useMemo, useState } from 'react';
import { Modal, Row, Col } from 'react-bootstrap';
import { Controller, useForm } from 'react-hook-form';
import PratusSelect, {
  type OptionType,
} from 'components/DesignSystems/New/PratusSelect';
import { StylishNewButton } from 'components/DesignSystems/New/StylishNewButton';
import type { LayerWithNodeMeta } from 'hooks/internalLayer/types';
import { formatDateTime } from 'components/IAP/Resources/utils/dateUtils';
import { useAllIncidents } from 'components/IAP/hooks/incidentHooks';
import { useUscgMapContext } from './UscgMapContext';
import { LayerFeaturesContent } from './LayerFeaturesContent';
import { InternalLayerVersionSelect } from './InternalLayerVersionSelect';
import { useAors } from 'hooks/useAors';
import { useUpdateInternalLayerMutation } from 'hooks/internalLayer/useUpdateInternalLayerMutation';
import { DraftNewVersionButton } from './DraftNewVersionButton';
import { NULL_VALUE } from 'constants/displayConstants';
import { useUpdateExternalLayerMutation } from 'hooks/externalLayer/useUpdateExternalLayerMutation';
import { useUpsertLayerNodeAssignmentMutation } from 'hooks/layerNode/useUpsertLayerNodeAssignmentMutation';
import { CategorySelect } from './CategorySelect';
import { useGroupGuid } from 'components/IAP/hooks/commonHooks';

// rymi-TODO: confirm with product if these are needed
const READ_ONLY_ROWS = [
  { label: 'Data Owner', value: '???' },
  { label: 'Update Frequency', value: '???' },
];

const DB_ROWS = [
  'Data Source',
  'Created',
  'Last Updated',
  'Region(s)',
  'Incident',
  'Type',
];

function getDbRowValue(label: string, layerWithType: LayerWithNodeMeta | null) {
  if (!layerWithType) return NULL_VALUE;

  const layer = layerWithType.layer;

  if (label === 'Data Source') {
    return layerWithType.type === 'internal' ? 'PRATUS' : 'ArcGIS Online';
  }
  if (label === 'Created') {
    return formatDateTime(layer.createdAt);
  }
  if (label === 'Last Updated') {
    return formatDateTime(layer.updatedAt);
  }
  if (label === 'Region(s)') {
    return layer.aorIds.length > 0 ? layer.aorIds.join(', ') : NULL_VALUE;
  }
  if (label === 'Incident') {
    return layer.incidentId ? layer.incidentId.toString() : NULL_VALUE;
  }
  if (label === 'Type') {
    return layerWithType.type === 'internal'
      ? 'Data layer (PRATUS)'
      : layerWithType.layer.itemType;
  }
  return NULL_VALUE;
}

type LayerDetailFormValues = {
  incidentId: string | null;
  aorIds: string[];
};

export function LayerDetailModal({
  show,
  onClose,
  layerWithType,
}: {
  show: boolean;
  onClose: () => void;
  layerWithType: LayerWithNodeMeta | null;
}) {
  const groupGuid = useGroupGuid();
  const { data: incidents = [] } = useAllIncidents();
  const incidentOptions = useMemo<OptionType[]>(
    () =>
      incidents.map((incident) => ({
        value: incident.id,
        label: incident.name,
      })),
    [incidents]
  );
  const { data: aorsData } = useAors();
  const aorOptions = useMemo<OptionType[]>(
    () =>
      (aorsData ?? []).map((aor) => ({
        value: aor.id,
        label: aor.name,
      })),
    [aorsData]
  );

  const layerDetailForm = useForm<LayerDetailFormValues>({
    defaultValues: {
      incidentId: layerWithType?.layer.incidentId
        ? String(layerWithType.layer.incidentId)
        : null,
      aorIds: layerWithType?.layer.aorIds.map((aorId) => String(aorId)) ?? [],
    },
  });

  const { control, handleSubmit, reset } = layerDetailForm;
  const [selectedFolderId, setSelectedFolderId] = useState<string | null>(
    layerWithType?.node?.parentId ?? null
  );
  useEffect(() => {
    reset({
      incidentId: layerWithType?.layer.incidentId
        ? String(layerWithType.layer.incidentId)
        : null,
      aorIds: layerWithType?.layer.aorIds.map((aorId) => String(aorId)) ?? [],
    });
  }, [layerWithType, reset]);

  const { zoomToGraphic } = useUscgMapContext();
  const updateInternalLayer = useUpdateInternalLayerMutation();
  const updateExternalLayer = useUpdateExternalLayerMutation();
  const upsertLayerNodeAssignment = useUpsertLayerNodeAssignmentMutation();

  const title = layerWithType?.layer.name;

  const onSubmit = handleSubmit(async (data) => {
    if (!layerWithType) return;

    const currentFolderId = layerWithType.node?.parentId ?? null;
    if (selectedFolderId !== currentFolderId) {
      await upsertLayerNodeAssignment.mutateAsync({
        kind: layerWithType.type,
        id: layerWithType.layer.id,
        groupGuid,
        targetFolderId: selectedFolderId,
      });
    }

    if (layerWithType?.type === 'external') {
      await updateExternalLayer.mutateAsync({
        externalLayerId: layerWithType.layer.id,
        incidentId: data.incidentId ?? null,
        aorIds: data.aorIds,
      });
    } else if (layerWithType?.type === 'internal') {
      await updateInternalLayer.mutateAsync({
        internalLayerId: layerWithType.layer.id,
        incidentId: data.incidentId ?? null,
        aorIds: data.aorIds,
      });
    }

    setSelectedFolderId(null);
    onClose();
  });

  return (
    <Modal show={show} onHide={onClose} size="xl">
      <form onSubmit={onSubmit}>
        <Modal.Header closeButton closeVariant="white">
          <div className="d-flex align-items-center gap-2">
            <Modal.Title>{title}</Modal.Title>
            {layerWithType?.type === 'internal' && (
              <InternalLayerVersionSelect layer={layerWithType.layer} />
            )}
            {layerWithType?.type === 'internal' && (
              <DraftNewVersionButton internalLayer={layerWithType.layer} />
            )}
          </div>
        </Modal.Header>
        <Modal.Body className="d-grid gap-3">
          <div className="d-grid">
            {DB_ROWS.map((label) => (
              <div
                key={label}
                className="d-flex justify-content-between align-items-center gap-2 py-2 border-neutral-500 border-bottom small"
              >
                <span>{label}</span>
                <span>{getDbRowValue(label, layerWithType)}</span>
              </div>
            ))}
            {READ_ONLY_ROWS.map((row) => (
              <div
                key={row.label}
                className="d-flex justify-content-between align-items-center gap-2 py-2 border-neutral-500 border-bottom small"
              >
                <span>{row.label}</span>
                <span>{row.value}</span>
              </div>
            ))}
          </div>

          <Row>
            <Col>
              <CategorySelect
                label="Category"
                value={selectedFolderId}
                onChange={setSelectedFolderId}
              />
            </Col>
            <Col>
              <Controller
                control={control}
                name="incidentId"
                render={({ field: { value, onChange } }) => {
                  const selectedIncidentOption =
                    incidentOptions.find(
                      (option) => String(option.value) === value
                    ) ?? null;

                  return (
                    <PratusSelect
                      label="Incident"
                      isClearable
                      options={incidentOptions}
                      value={selectedIncidentOption ?? undefined}
                      onChange={(val) => {
                        if (!val) {
                          onChange(null);
                          return;
                        }

                        const option = val as OptionType;
                        onChange(String(option.value));
                      }}
                    />
                  );
                }}
              />
            </Col>
            <Col>
              <Controller
                control={control}
                name="aorIds"
                render={({ field: { value, onChange } }) => {
                  const selectedOptions = aorOptions.filter((option) =>
                    (value ?? []).includes(String(option.value))
                  );

                  return (
                    <PratusSelect
                      label="AOR"
                      isMulti
                      options={aorOptions}
                      value={selectedOptions}
                      onChange={(val) => {
                        if (!val || !Array.isArray(val)) {
                          onChange([]);
                          return;
                        }

                        const options = val as OptionType[];
                        onChange(options.map((option) => String(option.value)));
                      }}
                    />
                  );
                }}
              />
            </Col>
          </Row>

          {layerWithType && (
            <div className="mt-2">
              <LayerFeaturesContent
                layer={layerWithType.layer}
                layerType={layerWithType.type}
                onZoomToExternalGraphic={(graphic) =>
                  zoomToGraphic({ graphic })
                }
              />
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <StylishNewButton
            type="button"
            className="button--secondary"
            onClick={onClose}
          >
            Cancel
          </StylishNewButton>

          <StylishNewButton
            type="submit"
            className="button--primary"
            disabled={
              updateInternalLayer.isPending ||
              updateExternalLayer.isPending ||
              upsertLayerNodeAssignment.isPending
            }
          >
            Save
          </StylishNewButton>
        </Modal.Footer>
      </form>
    </Modal>
  );
}
